// The account's time zone, chosen from a list (B-181): at sign-up and on the
// Account page. Searchable, with the zones most managers here live in first,
// and each named the way every time on the site will be ("America/Denver
// (MDT)"), so the choice reads like the result.

import { useMemo } from 'react';
import { Choice } from '../../kit/ui';
import { isTimeZone, zoneOptionLabel } from '@fsp/domain';

/** Offered first: the United States' zones, then a few the leagues here already span. */
const COMMON = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'America/Toronto',
  'Europe/London',
  'Australia/Sydney',
  'Pacific/Auckland',
];

/** Every zone this browser can name; the common list alone if it cannot say. */
function allZones(): string[] {
  try {
    const list = (
      Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
    ).supportedValuesOf?.('timeZone');
    if (list && list.length > 0) return list;
  } catch {
    // An older browser: the common zones are still a complete choice for most.
  }
  return COMMON;
}

export function TimeZoneSelect({
  value,
  onChange,
  label = 'Time zone',
  description,
  disabled,
}: {
  value: string | null;
  onChange: (zone: string) => void;
  label?: string;
  description?: string;
  disabled?: boolean;
}) {
  const data = useMemo(() => {
    const now = Date.now();
    const common = COMMON.filter(isTimeZone);
    const rest = allZones().filter((z) => !common.includes(z) && isTimeZone(z));
    // The chosen zone is always an option, even one this browser does not list.
    if (value !== null && !common.includes(value) && !rest.includes(value) && isTimeZone(value))
      rest.unshift(value);
    const item = (z: string) => ({ value: z, label: zoneOptionLabel(z, now) });
    return [
      { group: 'Common', items: common.map(item) },
      { group: 'Everywhere', items: rest.map(item) },
    ];
  }, [value]);

  return (
    <Choice
      label={label}
      description={description}
      placeholder="Search for a city or zone"
      options={data}
      value={value}
      onChange={(z) => {
        if (z !== null) onChange(z);
      }}
      required
      empty="No zone by that name"
      disabled={disabled}
    />
  );
}
